import { create } from 'zustand';
import { api } from '../services/api';

export type MediaType = 'image' | 'video' | 'audio';
export type MediaSource = 'upload' | 'design';

export type FormatCategoryType = 'square' | 'portrait' | 'story' | 'landscape' | null;
export type PlatformType = 'instagram' | 'facebook' | 'both' | null;

export type StockSource = 'unsplash' | 'pexels' | null;

export interface MediaFile {
  id: string;
  project_id: string;
  uploaded_by: string;
  post_id: string | null; // Legacy - kept for backward compatibility
  post_ids: string[]; // New: array of assigned post IDs
  flight_id: string | null; // Optional flight assignment
  filename: string;
  original_name: string;
  mime_type: string;
  size: number;
  url: string;
  delete_after: string | null;
  format_category: FormatCategoryType; // square, portrait, story, landscape
  // Stock photo fields
  source: 'upload' | 'stock' | 'design' | null;
  stock_source: StockSource; // unsplash, pexels
  stock_id: string | null;
  attribution: { photographer: string; photographer_url: string; source_url: string } | null;
  created_at: string;
  updated_at: string;
}

// Helper to check if media is from stock photo service
export function isStockPhoto(media: MediaFile): boolean {
  return media.source === 'stock' || media.stock_source !== null;
}

// Helper to determine media type from mime_type
export function getMediaType(mimeType: string): MediaType {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  return 'audio';
}

// Helper to get file extension icon
export function getMediaTypeLabel(type: MediaType): string {
  switch (type) {
    case 'image': return 'Bild';
    case 'video': return 'Video';
    case 'audio': return 'Audio';
  }
}

// Helper to determine if media is from Creator Studio (design export)
export function getMediaSource(url: string): MediaSource {
  return url.includes('/uploads/creator/') ? 'design' : 'upload';
}

// Helper to check if media is a design export
export function isDesignExport(url: string): boolean {
  return getMediaSource(url) === 'design';
}

// Helper to check if media is AI-generated
export function isAIGenerated(url: string): boolean {
  return url.includes('/ai-post-') || url.includes('/ai-image-');
}

// Format category labels
export const FORMAT_CATEGORY_LABELS: Record<string, { label: string; icon: string }> = {
  square: { label: 'Quadrat (1:1)', icon: '⬜' },
  portrait: { label: 'Hochformat (4:5)', icon: '📱' },
  story: { label: 'Story/Reel (9:16)', icon: '📲' },
  landscape: { label: 'Querformat (16:9)', icon: '🖥️' },
  unknown: { label: 'Unbekannt', icon: '❓' },
};

// Platform labels
export const PLATFORM_LABELS: Record<string, { label: string; icon: string; color: string }> = {
  instagram: { label: 'Instagram', icon: '📸', color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  facebook: { label: 'Facebook', icon: '👍', color: 'bg-blue-600' },
  both: { label: 'FB + IG', icon: '🔗', color: 'bg-purple-600' },
};

export interface CropData {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}

export interface CropFormat {
  id: string;
  aspectRatio: string;
  targetWidth?: number;
  targetHeight?: number;
}

export interface CropResult {
  croppedMedia: MediaFile;
  sourceMedia: {
    id: string;
    url: string;
    original_name: string;
  };
  cropInfo: {
    sourceWidth: number;
    sourceHeight: number;
    cropX: number;
    cropY: number;
    cropWidth: number;
    cropHeight: number;
    outputWidth: number;
    outputHeight: number;
    format: CropFormat;
  };
}

interface MediaState {
  media: MediaFile[];
  isLoading: boolean;
  isUploading: boolean;
  isCropping: boolean;
  uploadProgress: number;
  error: string | null;

  fetchMedia: (projectId: string, unassigned?: boolean, postId?: string, flightId?: string) => Promise<void>;
  uploadMedia: (projectId: string, file: File, flightId?: string) => Promise<MediaFile>;
  cropMedia: (mediaId: string, crop: CropData, format: CropFormat) => Promise<CropResult>;
  assignMedia: (mediaId: string, postId: string | null) => Promise<void>;
  assignMediaToMultiplePosts: (mediaId: string, postIds: string[]) => Promise<void>;
  unassignMediaFromPost: (mediaId: string, postId: string) => Promise<void>;
  assignToFlight: (mediaId: string, flightId: string | null) => Promise<void>;
  batchAssignToFlight: (mediaIds: string[], flightId: string | null) => Promise<void>;
  deleteMedia: (mediaId: string) => Promise<void>;
  saveDesignToLibrary: (designId: string, projectId: string, flightId?: string) => Promise<MediaFile>;
  clearMedia: () => void;
  clearError: () => void;
}

export const useMediaStore = create<MediaState>((set) => ({
  media: [],
  isLoading: false,
  isUploading: false,
  isCropping: false,
  uploadProgress: 0,
  error: null,

  fetchMedia: async (projectId: string, unassigned = false, postId?: string, flightId?: string) => {
    set({ isLoading: true, error: null });
    try {
      const params = new URLSearchParams({ project_id: projectId });
      if (unassigned) {
        params.append('unassigned', 'true');
      }
      if (postId) {
        params.append('post_id', postId);
      }
      if (flightId) {
        params.append('flight_id', flightId);
      }
      const response = await api.get<{ data: MediaFile[] }>(`/media?${params}`);
      // Ensure post_ids is always an array
      const mediaWithPostIds = response.data.map(m => ({
        ...m,
        post_ids: m.post_ids || [],
      }));
      set({ media: mediaWithPostIds, isLoading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Fehler beim Laden der Medien';
      set({ error: message, isLoading: false });
    }
  },

  uploadMedia: async (projectId: string, file: File, flightId?: string) => {
    set({ isUploading: true, uploadProgress: 0, error: null });
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('project_id', projectId);
      if (flightId) {
        formData.append('flight_id', flightId);
      }

      const token = api.getAccessToken();
      if (!token) {
        throw new Error('Nicht eingeloggt. Bitte erneut anmelden.');
      }

      const response = await fetch('/api/media/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
        body: formData,
      });

      // Handle token expiry - try to refresh
      if (response.status === 401) {
        const errorData = await response.json();
        if (errorData.code === 'TOKEN_EXPIRED') {
          const refreshed = await api.refreshAccessToken();
          if (refreshed) {
            // Retry with new token
            const newToken = api.getAccessToken();
            const retryResponse = await fetch('/api/media/upload', {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${newToken}`,
              },
              credentials: 'include',
              body: formData,
            });

            if (!retryResponse.ok) {
              const retryData = await retryResponse.json();
              throw new Error(retryData.error || 'Upload fehlgeschlagen');
            }

            const retryData = await retryResponse.json();
            const newMedia = retryData.data as MediaFile;

            set((state) => ({
              media: [newMedia, ...state.media],
              isUploading: false,
              uploadProgress: 100,
            }));

            return newMedia;
          }
        }
        throw new Error(errorData.error || 'Nicht autorisiert');
      }

      if (!response.ok) {
        let errorMessage = 'Upload fehlgeschlagen';
        try {
          const data = await response.json();
          errorMessage = data.error || `Fehler ${response.status}: ${response.statusText}`;
          console.error('Upload error response:', data);
        } catch {
          errorMessage = `Fehler ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      const newMedia = data.data as MediaFile;

      set((state) => ({
        media: [newMedia, ...state.media],
        isUploading: false,
        uploadProgress: 100,
      }));

      return newMedia;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Upload fehlgeschlagen';
      set({ error: message, isUploading: false, uploadProgress: 0 });
      throw error;
    }
  },

  cropMedia: async (mediaId: string, crop: CropData, format: CropFormat) => {
    set({ isCropping: true, error: null });
    try {
      const response = await api.post<{ data: CropResult['croppedMedia'] & { sourceMedia: CropResult['sourceMedia']; cropInfo: CropResult['cropInfo'] } }>(`/media/${mediaId}/crop`, {
        crop,
        format,
      });

      const { sourceMedia, cropInfo, ...croppedMedia } = response.data;

      // Add the new cropped media to the store
      set((state) => ({
        media: [{ ...croppedMedia, post_ids: croppedMedia.post_ids || [] }, ...state.media],
        isCropping: false,
      }));

      return {
        croppedMedia: { ...croppedMedia, post_ids: croppedMedia.post_ids || [] },
        sourceMedia,
        cropInfo,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Zuschneiden fehlgeschlagen';
      set({ error: message, isCropping: false });
      throw error;
    }
  },

  assignMedia: async (mediaId: string, postId: string | null) => {
    try {
      const response = await api.put<{ data: MediaFile }>(`/media/${mediaId}/assign`, { post_id: postId });
      const updatedMedia = response.data;
      set((state) => ({
        media: state.media.map((m) =>
          m.id === mediaId ? { ...updatedMedia, post_ids: updatedMedia.post_ids || [] } : m
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Zuweisung fehlgeschlagen';
      set({ error: message });
      throw error;
    }
  },

  assignMediaToMultiplePosts: async (mediaId: string, postIds: string[]) => {
    try {
      const response = await api.put<{ data: MediaFile }>(`/media/${mediaId}/assign`, { post_ids: postIds });
      const updatedMedia = response.data;
      set((state) => ({
        media: state.media.map((m) =>
          m.id === mediaId ? { ...updatedMedia, post_ids: updatedMedia.post_ids || [] } : m
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Zuweisung fehlgeschlagen';
      set({ error: message });
      throw error;
    }
  },

  unassignMediaFromPost: async (mediaId: string, postId: string) => {
    try {
      const response = await api.delete<{ data: MediaFile }>(`/media/${mediaId}/unassign/${postId}`);
      const updatedMedia = response.data;
      set((state) => ({
        media: state.media.map((m) =>
          m.id === mediaId ? { ...updatedMedia, post_ids: updatedMedia.post_ids || [] } : m
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Zuweisung konnte nicht entfernt werden';
      set({ error: message });
      throw error;
    }
  },

  assignToFlight: async (mediaId: string, flightId: string | null) => {
    try {
      const response = await api.put<{ data: MediaFile }>(`/media/${mediaId}/flight`, { flight_id: flightId });
      const updatedMedia = response.data;
      set((state) => ({
        media: state.media.map((m) =>
          m.id === mediaId ? { ...updatedMedia, post_ids: updatedMedia.post_ids || [] } : m
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Flight-Zuweisung fehlgeschlagen';
      set({ error: message });
      throw error;
    }
  },

  batchAssignToFlight: async (mediaIds: string[], flightId: string | null) => {
    try {
      await api.put('/media/batch/flight', { media_ids: mediaIds, flight_id: flightId });
      set((state) => ({
        media: state.media.map((m) =>
          mediaIds.includes(m.id) ? { ...m, flight_id: flightId } : m
        ),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Batch-Flight-Zuweisung fehlgeschlagen';
      set({ error: message });
      throw error;
    }
  },

  deleteMedia: async (mediaId: string) => {
    try {
      await api.delete(`/media/${mediaId}`);
      set((state) => ({
        media: state.media.filter((m) => m.id !== mediaId),
      }));
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Löschen fehlgeschlagen';
      set({ error: message });
      throw error;
    }
  },

  saveDesignToLibrary: async (designId: string, projectId: string, flightId?: string) => {
    set({ isUploading: true, error: null });
    try {
      const response = await api.post<{ success: boolean; media: MediaFile }>('/media/save-design', {
        design_id: designId,
        project_id: projectId,
        flight_id: flightId,
      });

      const newMedia = {
        ...response.media,
        post_ids: [],
      };

      set((state) => ({
        media: [newMedia, ...state.media],
        isUploading: false,
      }));

      return newMedia;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Design speichern fehlgeschlagen';
      set({ error: message, isUploading: false });
      throw error;
    }
  },

  clearMedia: () => set({ media: [] }),

  clearError: () => set({ error: null }),
}));
